import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { combineLatest, map } from 'rxjs';
import { UserPermission } from '../constants/permission.constants.enum';
import { DowntimeService } from '../services/downtime.service';
import { InsightsService } from '../services/insights.service';
import { NavigationService } from '../services/navigation.service';
import { UserService } from '../services/user.service';

export const groupPermissionGuard: CanActivateFn = (route, _) => {
  const nav = inject(NavigationService);
  const router = inject(Router);
  const location = inject(Location);
  const insights = inject(InsightsService);
  const downtime = inject(DowntimeService);
  const user = inject(UserService);
  return combineLatest([nav.navItems$, downtime.downtime$, user.permission$, user.hasNoPermissions$]).pipe(
    map(([items, d, p, noPerms]) => {
      console.log(d.isDown, d.downtimeMessage, items, p);
      if (noPerms) {
        insights.trackPageView('Not Authorized', '/not-authorized', { service: 'MS Graph group guard' });
        return router.parseUrl('/not-authorized');
      }
      const isAdmin = p.includes(UserPermission.MedNecScreen_ISAdmin) || p.includes(UserPermission.MedNecScreen_Quorum_ISAdmin);
      if (!isAdmin && d.isDown) {
        insights.trackPageView('Downtime', '/unavailable', { service: 'MS Graph group guard' });
        return router.parseUrl('/unavailable');
      }
      const navItem = nav.findNavItemForPath(items, route.routeConfig?.path);
      if (!!navItem?.visible) {
        insights.trackPageView(navItem.label ?? '', '/' + navItem.routerLink, { service: 'MS Graph group guard' });
        return true;
      }
      const nextLinkIndex = nav.flattenedNavItems.findIndex(item => item.routerLink === navItem?.routerLink) + 1;
      // navigating directly to a route without permission
      if (nextLinkIndex === 0 && route.routeConfig?.path === location.path().slice(1)) {
        nav.unauthorizedNavItem.set(navItem);
        insights.trackPageView(navItem?.label ?? '', '/not-authorized', { service: 'MS Graph group guard' });
        return router.parseUrl('/not-authorized');
      }
      // no permission for requested page, try the next page in the menu
      if (nav.flattenedNavItems[nextLinkIndex].routerLink) {
        insights.trackPageView(nav.flattenedNavItems[nextLinkIndex].label ?? '', '/' + nav.flattenedNavItems[nextLinkIndex].routerLink, { service: 'MS Graph group guard' });
        return router.parseUrl(nav.flattenedNavItems[nextLinkIndex].routerLink);
      } else if (nav.flattenedNavItems[nextLinkIndex].url) {
        // if the next page in the menu is an external link,
        // dispose the router and navigate
        router.dispose();
        insights.trackTrace(`external navigation to: ${nav.flattenedNavItems[nextLinkIndex].url}`, { service: 'MS Graph group guard' });
        window.location.href = nav.flattenedNavItems[nextLinkIndex].url;
        return false;
      }
      return router.parseUrl('/not-authorized');
    })
  )
}
